// ============================================
// Payments Hooks - TanStack Query
// ============================================

import { useQuery } from '@tanstack/react-query';
import type { PaymentListParams, PaymentStatus, UUID } from '@conai/shared-types';
import { useApi, usePaymentsService } from '../providers/ApiProvider';
import { queryKeys } from '../config/query-client';

/**
 * Response của GET /payments/stats/ — nguồn đếm giao dịch CHUẨN.
 * Dùng chung get_queryset/filter_queryset với /payments/ nên cùng scope store.
 * Lọc ngày theo `requested_at`, bỏ trống ngày = all-time.
 */
export interface PaymentStatsResponse {
  status_counts: Record<string, number>;
  paid_count: number;
  paid_amount: string;
  refunded_count: number;
  refunded_amount: string;
  total_count: number;
  applied_filters?: Record<string, unknown>;
}

export function usePaymentStats(
  params: { store_id?: string; start_date?: string; end_date?: string } = {},
  options?: { enabled?: boolean }
) {
  const api = useApi();

  return useQuery({
    queryKey: ['payments', 'stats', params] as const,
    queryFn: () =>
      api.client.get<PaymentStatsResponse>('/payments/stats/', {
        store_id: params.store_id,
        start_date: params.start_date,
        end_date: params.end_date,
      }),
    enabled: options?.enabled !== false,
  });
}

export function usePayments(params: PaymentListParams = {}) {
  const paymentsService = usePaymentsService();

  return useQuery({
    queryKey: queryKeys.payments.list(params as Record<string, unknown>),
    queryFn: () => paymentsService.list(params),
    staleTime: 0, // always fetch fresh when page/filters change
    retry: 1,     // only retry once — fail fast so user sees real error
  });
}

export function usePaymentsByStatus(
  status: PaymentStatus,
  params: Omit<PaymentListParams, 'status'> = {}
) {
  const paymentsService = usePaymentsService();

  return useQuery({
    queryKey: queryKeys.payments.list({ ...params, status } as Record<string, unknown>),
    queryFn: () => paymentsService.getByStatus(status, params),
  });
}

export function usePaymentsByOrder(orderId: UUID, options?: { enabled?: boolean }) {
  const paymentsService = usePaymentsService();

  return useQuery({
    queryKey: queryKeys.payments.byOrder(orderId),
    queryFn: () => paymentsService.listByOrder(orderId),
    enabled: options?.enabled !== false && !!orderId,
  });
}
